import type { DataTableColumn } from '@/ui/DataTable';

/**
 * As regras de quais colunas uma listagem mostra, e em que ordem.
 *
 * Funcoes puras, de proposito: elas decidem layout de tabela e sao o tipo de coisa
 * que se conserta uma vez e vale em toda tela. Nada aqui grava nem le preferencia —
 * quem persiste e `useTableViewPreferences`.
 *
 * A ORDEM ESTRUTURAL NAO E NEGOCIAVEL, e e o que estas funcoes protegem:
 *
 *   [selecao] → [colunas comuns] → [situacao] → [cadastro] → [sincronizacao] → [acoes]
 *
 * `CLAUDE.md`, "Regras de interface", item 10 fixa as duas pontas: selecao sempre
 * primeira, acoes sempre ultima. O meio segue a mesma logica de leitura — quem varre
 * a tabela procura o dado primeiro e o julgamento ("esta ativo?", "quando
 * sincronizou?") depois. Deixar o usuario arrastar a coluna de situacao para o meio
 * pareceria liberdade e produziria tabela ilegivel na tela do colega ao lado.
 */

export interface TableViewColumn<T> extends DataTableColumn<T> {
  /** Obrigatorio aqui: e a chave da preferencia gravada. `header` muda, `id` nao. */
  id: string;
  /** `false` prende a coluna na tabela. Padrao: pode ocultar. */
  canHide?: boolean;
  /** `false` prende a coluna na posicao. Padrao: pode reordenar. */
  canReorder?: boolean;
  /** `false` nasce oculta. Padrao: nasce visivel. */
  defaultVisible?: boolean;
  /** Coluna sem a qual a linha nao se identifica. Nunca oculta, mesmo se pedirem. */
  isCritical?: boolean;
  /** Coluna de acoes em linha. Sempre a ultima. */
  isActionColumn?: boolean;
  /** Coluna de situacao. Fica no fim do bloco de dados. */
  isStatusColumn?: boolean;
  /** Coluna de data de cadastro. Depois da situacao. */
  isCadastroColumn?: boolean;
  /**
   * Coluna "Ultima sincronizacao" das entidades que vem de integracao externa.
   * Ultima coluna util, logo antes das acoes. Detectada tambem pelo `id`, em
   * `travarColunasEstruturais`, para a regra valer mesmo se a tela esquecer a marca.
   */
  isSyncColumn?: boolean;
  /**
   * Filtros que esta coluna explica. Se um deles estiver ativo e a coluna estiver
   * oculta, a tela avisa — senao o usuario ve uma lista recortada sem enxergar o
   * critério do recorte, e conclui que o sistema perdeu registros.
   */
  linkedFilterKeys?: readonly string[];
}

/** Layout gravado de uma listagem. */
export interface LayoutDaTabela {
  visiveis: string[];
  ordem: string[];
}

export interface EstadoResolvido<T> {
  /** As colunas que vao para a `DataTable`, na ordem final. */
  orderedColumns: TableViewColumn<T>[];
  /** Colunas ocultas cujo filtro esta ativo. A tela avisa sobre elas. */
  hiddenFilteredColumns: TableViewColumn<T>[];
  visibleColumnIds: string[];
  columnOrder: string[];
}

function semRepetir(valores: readonly string[]): string[] {
  return Array.from(new Set(valores.filter(Boolean)));
}

export function mesmaLista(a: readonly string[] = [], b: readonly string[] = []): boolean {
  return a.length === b.length && a.every((valor, indice) => valor === b[indice]);
}

export function colunasVisiveisPorPadrao<T>(colunas: readonly TableViewColumn<T>[]): string[] {
  return colunas
    .filter((coluna) => coluna.defaultVisible !== false || coluna.isCritical)
    .map((coluna) => coluna.id);
}

export function ordemPadrao<T>(colunas: readonly TableViewColumn<T>[]): string[] {
  return colunas.map((coluna) => coluna.id);
}

/** Aplica a ordem estrutural descrita no topo deste arquivo. */
export function aplicarOrdemEstrutural<T>(
  colunas: readonly TableViewColumn<T>[],
): TableViewColumn<T>[] {
  const selecao = colunas.filter((c) => c.isSelectionColumn);
  const comuns = colunas.filter(
    (c) =>
      !c.isSelectionColumn &&
      !c.isActionColumn &&
      !c.isStatusColumn &&
      !c.isCadastroColumn &&
      !c.isSyncColumn,
  );
  const situacao = colunas.filter((c) => c.isStatusColumn);
  const cadastro = colunas.filter((c) => c.isCadastroColumn);
  const sincronizacao = colunas.filter((c) => c.isSyncColumn);
  const acoes = colunas.filter((c) => c.isActionColumn);

  return [...selecao, ...comuns, ...situacao, ...cadastro, ...sincronizacao, ...acoes];
}

/**
 * Fecha as travas das colunas estruturais.
 *
 * Chamado sempre, e nao deixado a cargo de cada tela: cinco telas declarando as
 * mesmas quatro flags a mao acabariam com uma delas esquecendo — e o sintoma seria a
 * caixa de selecao no meio da tabela, ou a coluna de situacao arrastavel numa tela e
 * fixa na outra.
 */
export function travarColunasEstruturais<T>(
  colunas: readonly TableViewColumn<T>[],
): TableViewColumn<T>[] {
  return colunas.map((coluna) => {
    if (coluna.isSelectionColumn || coluna.isActionColumn) {
      return { ...coluna, canHide: false, canReorder: false, isCritical: true };
    }
    if (coluna.isSyncColumn || coluna.id === 'ultima_sincronizacao') {
      return { ...coluna, isSyncColumn: true, canReorder: false };
    }
    /**
     * SITUACAO E CADASTRO TAMBEM SAO PRESAS, e isso apareceu exercitando o modal.
     *
     * `aplicarOrdemEstrutural` ja devolvia as duas para o bloco delas, entao arrastar a
     * coluna de situacao para o meio da tabela nao tinha efeito nenhum — mas as setas
     * ficavam habilitadas. Controle que parece funcionar e nao faz nada e pior que
     * controle ausente: o usuario clica, nao ve mudanca e conclui que a tela travou.
     * Marcar aqui faz a interface dizer a verdade que a ordenacao ja impunha.
     */
    if (coluna.isStatusColumn || coluna.isCadastroColumn) {
      return { ...coluna, canReorder: false };
    }
    return coluna;
  });
}

/**
 * Junta as colunas declaradas pela tela com o layout gravado e devolve o que a
 * `DataTable` recebe.
 *
 * DUAS PROTECOES QUE PARECEM DETALHE E SALVAM A TELA DE PARECER QUEBRADA:
 *
 * 1. COLUNA NOVA APARECE PARA QUEM JA TEM LAYOUT GRAVADO. Sem isto, acrescentar uma
 *    coluna ao codigo a tornaria invisivel justamente para quem usa o sistema todo
 *    dia — a preferencia gravada nao a menciona, e "nao mencionada" seria lida como
 *    "oculta". Coluna que o layout nunca viu e tratada como nova, e entra visivel.
 * 2. COLUNA CRITICA VOLTA MESMO SE O LAYOUT A OMITIR. Layout gravado antes de a
 *    coluna virar critica, ou mexido a mao no armazenamento local, nao pode produzir
 *    tabela em que a linha nao se identifica.
 */
export function resolverEstadoDaTabela<T>(
  colunas: readonly TableViewColumn<T>[],
  layout?: Partial<LayoutDaTabela> | null,
  valoresDosFiltros?: Record<string, string>,
): EstadoResolvido<T> {
  const porId = new Map(colunas.map((coluna) => [coluna.id, coluna]));
  const visiveisPorPadrao = colunasVisiveisPorPadrao(colunas);
  const visiveisGravadas = layout?.visiveis ?? [];
  const ordemGravada = layout?.ordem ?? [];

  const conhecidas = new Set([...visiveisGravadas, ...ordemGravada]);
  const novas = visiveisPorPadrao.filter((id) => !conhecidas.has(id));

  const visiveis = semRepetir(
    visiveisGravadas.length > 0 ? [...visiveisGravadas, ...novas] : visiveisPorPadrao,
  );
  const visiveisProtegidas = new Set(
    semRepetir([...visiveis, ...colunas.filter((c) => c.isCritical).map((c) => c.id)]),
  );

  const ordemCompleta = semRepetir([
    ...ordemGravada.filter((id) => porId.has(id)),
    ...ordemPadrao(colunas),
  ]);

  const naOrdem = ordemCompleta
    .map((id) => porId.get(id))
    .filter((coluna): coluna is TableViewColumn<T> => Boolean(coluna));

  const orderedColumns = aplicarOrdemEstrutural(
    naOrdem.filter((coluna) => visiveisProtegidas.has(coluna.id)),
  );

  const hiddenFilteredColumns = colunas.filter((coluna) => {
    if (visiveisProtegidas.has(coluna.id)) {
      return false;
    }
    if (!coluna.linkedFilterKeys?.length) {
      return false;
    }
    return coluna.linkedFilterKeys.some((chave) => {
      const valor = valoresDosFiltros?.[chave];
      return typeof valor === 'string' && valor.trim().length > 0;
    });
  });

  return {
    orderedColumns,
    hiddenFilteredColumns,
    visibleColumnIds: orderedColumns.map((coluna) => coluna.id),
    columnOrder: aplicarOrdemEstrutural(naOrdem).map((coluna) => coluna.id),
  };
}
